import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { TOUR_STEPS, TourStep } from "./tourSteps";

const STORAGE_KEY = "foo_inventory_tour";

interface TourState {
  currentStep: number;
  dismissed: boolean;
  completed: boolean;
}

interface TourContextValue {
  /** Whether the tour is currently active */
  isActive: boolean;
  /** Current step index */
  currentStep: number;
  /** Current step data */
  step: TourStep | null;
  /** Total steps */
  totalSteps: number;
  /** Start or restart the tour from the beginning */
  startTour: () => void;
  /** Resume the tour from where the user left off */
  resumeTour: () => void;
  /** Advance to next step */
  nextStep: () => void;
  /** Go back one step */
  prevStep: () => void;
  /** Dismiss/close the tour (can be resumed) */
  dismissTour: () => void;
  /** Whether the tour has been completed at least once */
  hasCompleted: boolean;
  /** Whether this is the first visit (never seen tour) */
  isFirstVisit: boolean;
}

const TourContext = createContext<TourContextValue>({
  isActive: false,
  currentStep: 0,
  step: null,
  totalSteps: TOUR_STEPS.length,
  startTour: () => {},
  resumeTour: () => {},
  nextStep: () => {},
  prevStep: () => {},
  dismissTour: () => {},
  hasCompleted: false,
  isFirstVisit: true,
});

export const useInventoryTour = () => useContext(TourContext);

function loadState(): TourState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { currentStep: 0, dismissed: false, completed: false };
}

function saveState(state: TourState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

function isDesktop(): boolean {
  return window.innerWidth >= 992;
}

export const InventoryTourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const history = useHistory();
  const location = useLocation();

  const [persisted] = useState(() => loadState());
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(persisted.currentStep);
  const [hasCompleted, setHasCompleted] = useState(persisted.completed);
  const [isFirstVisit] = useState(!persisted.dismissed && !persisted.completed && persisted.currentStep === 0);

  // Auto-show tour on first visit (desktop only)
  useEffect(() => {
    if (isFirstVisit && location.pathname.startsWith("/inventory") && isDesktop()) {
      setIsActive(true);
    }
    // eslint-disable-next-line
  }, []);

  // Persist state changes
  useEffect(() => {
    saveState({
      currentStep: isActive ? currentStep : currentStep,
      dismissed: !isActive && !hasCompleted && currentStep > 0,
      completed: hasCompleted,
    });
  }, [currentStep, isActive, hasCompleted]);

  const step = isActive ? TOUR_STEPS[currentStep] || null : null;

  const startTour = useCallback(() => {
    if (!isDesktop()) return;
    setCurrentStep(0);
    setIsActive(true);
    saveState({ currentStep: 0, dismissed: false, completed: hasCompleted });
    // Navigate to the first step's route
    if (TOUR_STEPS[0]?.route) {
      history.push(TOUR_STEPS[0].route);
    }
  }, [history, hasCompleted]);

  const resumeTour = useCallback(() => {
    if (!isDesktop()) return;
    setIsActive(true);
    saveState({ currentStep, dismissed: false, completed: hasCompleted });
    const stepData = TOUR_STEPS[currentStep];
    if (stepData?.route) {
      history.push(stepData.route);
    }
  }, [history, currentStep, hasCompleted]);

  const nextStep = useCallback(() => {
    const nextIdx = currentStep + 1;
    if (nextIdx >= TOUR_STEPS.length) {
      // Tour complete
      setIsActive(false);
      setHasCompleted(true);
      saveState({ currentStep: 0, dismissed: false, completed: true });
      return;
    }

    const currentStepData = TOUR_STEPS[currentStep];
    const nextStepData = TOUR_STEPS[nextIdx];

    setCurrentStep(nextIdx);

    // Navigate if the current step has navigateTo or if the next step is on a different route
    if (currentStepData?.navigateTo) {
      history.push(currentStepData.navigateTo);
    } else if (nextStepData?.route !== currentStepData?.route) {
      history.push(nextStepData.route);
    }
  }, [currentStep, history]);

  const prevStep = useCallback(() => {
    if (currentStep <= 0) return;
    const prevIdx = currentStep - 1;
    const prevStepData = TOUR_STEPS[prevIdx];
    setCurrentStep(prevIdx);
    if (prevStepData?.route !== TOUR_STEPS[currentStep]?.route) {
      history.push(prevStepData.route);
    }
  }, [currentStep, history]);

  const dismissTour = useCallback(() => {
    setIsActive(false);
    saveState({ currentStep, dismissed: true, completed: hasCompleted });
  }, [currentStep, hasCompleted]);

  return (
    <TourContext.Provider
      value={{
        isActive,
        currentStep,
        step,
        totalSteps: TOUR_STEPS.length,
        startTour,
        resumeTour,
        nextStep,
        prevStep,
        dismissTour,
        hasCompleted,
        isFirstVisit,
      }}
    >
      {children}
    </TourContext.Provider>
  );
};
